import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useNavigate } from 'react-router-dom';
import AuthModal from './AuthModal';
import AgentSelectionModal from './AgentSelectionModal';
import useAgentSelectionCheck from './useAgentSelectionCheck';

const SubscriptionGuard = ({ children, showAuthPrompt = true }) => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const { isRequired: agentSelectionRequired, loading: agentSelectionLoading } = useAgentSelectionCheck();

  // Check subscription status when user is available
  useEffect(() => {
    const checkSubscription = async () => {
      console.log('🔍 SubscriptionGuard triggered, user:', user?.email || 'null');
      if (!user) {
        setSubscriptionData(null);
        return;
      }

      // Check if user just completed onboarding
      const urlParams = new URLSearchParams(window.location.search);
      const onboardingComplete = urlParams.get('onboarding_complete');
      console.log('🔍 SubscriptionGuard onboarding complete param:', onboardingComplete);
      if (onboardingComplete) {
        console.log('✅ SubscriptionGuard: Onboarding complete, skipping check');
        return; // Skip subscription check if onboarding is complete
      }

      try {
        setSubscriptionLoading(true);
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
        const response = await fetch(`${backendUrl}/api/subscription/current`, {
          credentials: 'include',
        });

        if (response.ok) {
          const data = await response.json();
          console.log('🔍 SubscriptionGuard response:', data);
          setSubscriptionData(data);
          
          // If user needs subscription selection, redirect to pricing
          if (data.needs_subscription_selection) {
            console.log('🔴 SubscriptionGuard: User needs subscription, redirecting to pricing');
            navigate('/pricing');
            return;
          } else {
            console.log('✅ SubscriptionGuard: User has subscription, no redirect');
          }
        }
      } catch (error) {
        console.error('Error checking subscription:', error);
      } finally {
        setSubscriptionLoading(false);
      }
    };

    checkSubscription();
  }, [user, navigate]);

  // Show loading state
  if (authLoading || subscriptionLoading || agentSelectionLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-emerald-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  // Show auth prompt if not authenticated
  if (!user && showAuthPrompt) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-white mb-4">Welcome to ImmigrantAI</h1>
            <p className="text-gray-400 mb-6">
              Please sign in to access your personalized immigration assistance and chat history.
            </p>
            <button
              onClick={() => setShowAuthModal(true)}
              className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-3 rounded-lg font-medium transition-colors"
            >
              Sign In to Continue
            </button>
          </div>
        </div>
        
        <AuthModal 
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          initialMode="signin"
        />
      </div>
    );
  }

  // If user is authenticated but needs subscription selection, this will be handled by the redirect above
  // If user needs agent selection, show agent selection modal
  if (user && agentSelectionRequired) {
    return (
      <>
        {children}
        <AgentSelectionModal
          isOpen={agentSelectionRequired}
          onClose={() => {}} // Don't allow closing without selection
          onComplete={() => window.location.reload()}
        />
      </>
    );
  }

  return children;
};

export default SubscriptionGuard;